// Typed wrapper around fetch. Uses HttpOnly cookie auth (no headers).
// Throws ApiError on non-2xx; caller may catch for status-specific UX.

import { dateRangeQs, compareQs, type DateRangeValue, type CompareValue } from "./dateRange";
import { apiErrorDetail } from "./api/apiError";
import type {
  AccessResponse,
  AttributionByCampaignResponse,
  AttributionByChannelResponse,
  AdminGrantRow,
  AdminBrandMappingList,
  SetWarehouseMappingResponse,
  AdminOrgRow,
  AdminUserRow,
  AuditDetail,
  AuditKind,
  AuditLogResponse,
  FoundationAuditResult,
  AuditReport,
  CountryPerformance,
  AuditSummary,
  BillingStatusResponse,
  BrandClvResponse,
  BrandDetail,
  OverviewPayload,
  BrandImpactParams,
  BrandImpactResponse,
  CategoryInsightsParams,
  CategoryInsightsResponse,
  ProductInsightsParams,
  ProductInsightsResponse,
  ProductLtvSort,
  ProductLtvResponse,
  ProductCohortRetentionResponse,
  BrandSummary,
  CampaignDetail,
  CampaignSummary,
  BrandLearningsResponse,
  CohortRetentionResponse,
  CustomerCohortKpis,
  DeliverabilityAlertsResponse,
  SubscriberFunnelResponse,
  ReachabilityResponse,
  EarlyRepeatResponse,
  DiscountLtvResponse,
  PortfolioDeliverabilityResponse,
  ListHealthResponse,
  PortfolioTopActionsResponse,
  RecentExecutedResponse,
  RevenueTrendResponse,
  RfmTierResponse,
  SegmentDistributionResponse,
  CreateBrandPayload,
  CreateBrandResponse,
  SellerAccountsResponse,
  CreateSellerAccountPayload,
  CreateSellerAccountResponse,
  BindingsResponse,
  CreateBindingPayload,
  CreateBindingResponse,
  AnalyticsRefreshResponse,
  FlowDeployResponse,
  FlowDetailResponse,
  FlowsListResponse,
  Goal,
  GoalCreatePayload,
  GoalPatchPayload,
  InviteMemberPayload,
  InviteMemberResponse,
  ResendInviteResponse,
  OrgInvite,
  OrgMemberGrant,
  OrgAuditItem,
  MeResponse,
  MyOrgsResponse,
  Monitor,
  MonitorBreach,
  MonitorCreatePayload,
  MonitorPatchPayload,
  MonitorRunResponse,
  NextBestProductParams,
  NextBestProductResponse,
  TopCopurchasePairsParams,
  TopCopurchasePairsResponse,
  OrgMeResponse,
  OrgRole,
  PaginatedSegments,
  PaginatedTemplates,
  PerformanceEntityType,
  PerformanceResponse,
  ChannelBreakdownResponse,
  SmsEconomicsResponse,
  SmsBilling,
  RevenueSegmentsResponse,
  PortfolioPerformanceResponse,
  PromotionsResponse,
  SubjectsResponse,
  SynthesisResponse,
  ComposerGenerateResponse,
  ComposerGeneratePayload,
  ComposerApprovePayload,
  ComposerApproveResponse,
  AudiencesResponse,
  TemplateSlotMapSummary,
  UpdateMemberRoleResponse,
  VersionResponse,
  WeeklyReviewResponse,
  WeeklyReviewTriggerResponse,
  SendTimeFullResponse,
  SendTimeBrandSummaryResponse,
  SendTimeProfileResponse,
  SendTimeParams,
  DueCustomersResponse,
  CrossSellDateSummaryResponse,
  PlaybooksListResponse,
  PlaybookSummary,
  PlaybookInstantiateResponse,
  PlaybookCategory,
  SetupChecklistResponse,
  KlaviyoPaPrediction,
  KlaviyoPaSummary,
  ForwardClvPrediction,
  ForwardClvSummary,
  SegmentPreview,
  KlaviyoFreshnessResponse,
  DormantHighClvResponse,
  SegmentInsightsResponse,
  SegmentSuppressionAuditResponse,
  SegmentsOverlapResponse,
  CustomerSearchResponse,
  ReplenishmentResponse,
  LiveWriteResult,
  ChatMessagesResponse,
  ChatSessionsListResponse,
  ChatPermissionResolveResponse,
  TranslateEntityType,
  TranslateEngine,
  TranslateTargetsResponse,
  TranslationDraft,
  TranslationDraftSummary,
  TranslationDraftStatus,
  TranslationEdit,
  TranslateApproveResponse,
  TranslationSettings,
} from "./types";

export class ApiError extends Error {
  status: number;
  /** The response body exactly as received. Unchanged — callers that already
   *  parse it themselves keep working. */
  body: string;
  path: string;
  /**
   * The server's own sentence, when it sent one — `{"detail": "..."}` unwrapped
   * — otherwise the raw body (see `apiErrorDetail`).
   *
   * `message` carries it too, so anything that renders `error.message` shows
   * "unknown metric 'revenu'" rather than `{"detail":"unknown metric
   * 'revenu'"}`. The reporting compiler writes those messages precisely so the
   * reader learns what they mistyped; wrapping them in JSON on screen throws
   * that away. `detail` is exposed separately for surfaces that want the
   * sentence alone, without the `API 400 on /path:` prefix.
   */
  detail: string;
  constructor(status: number, body: string, path: string) {
    const detail = apiErrorDetail(body);
    super(`API ${status} on ${path}: ${detail.slice(0, 200)}`);
    this.status = status;
    this.body = body;
    this.path = path;
    this.detail = detail;
  }
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const text = await response.text();
    // Global session-expiry handling: a 401 on any authed call means the
    // session lapsed mid-use. Redirect to /login to re-auth instead of letting
    // each page surface its own (often misleading) error — e.g. Campaigns
    // showed "no active Klaviyo connection" for what was really an expired
    // session. Exclusions: the startup /api/me probe (AuthContext owns that and
    // renders the login route via RequireAuth), and never redirect while
    // already on /login (loop-guard).
    if (
      response.status === 401 &&
      path !== "/api/me" &&
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/login")
    ) {
      window.location.assign("/login");
    }
    throw new ApiError(response.status, text, path);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export const api = {
  me: () => request<MeResponse>("GET", "/api/me"),
  access: () => request<AccessResponse>("GET", "/api/me/access"),
  version: () => request<VersionResponse>("GET", "/api/version"),

  listBrands: () => request<BrandSummary[]>("GET", "/api/brands"),
  // Cross-org brand list (SuperAdmin) — powers the "All organisations" view.
  listBrandsAcrossOrgs: () =>
    request<BrandSummary[]>("GET", "/api/brands?scope=all"),

  // Org picker.
  listMyOrgs: () => request<MyOrgsResponse>("GET", "/api/me/orgs"),
  setActiveOrg: (orgId: string) =>
    request<{ ok: boolean; active_org_id: string; role: string }>(
      "POST",
      "/api/me/active-org",
      { org_id: orgId },
    ),
  getBrand: (slug: string) => request<BrandDetail>("GET", `/api/brands/${slug}`),

  // Per-brand live-write toggle. enabled=false ⇒ force_dry_run (write OFF).
  setLiveWrite: (slug: string, enabled: boolean) =>
    request<LiveWriteResult>("PATCH", `/api/brands/${slug}/live-write`, { enabled }),

  // SMS cost-per-credit (+ currency) → derives SMS $ cost + ROAS from credits.
  // cost_per_credit=null clears it (back to credits-only).
  setSmsBilling: (slug: string, costPerCredit: number | null, currency: string) =>
    request<{ slug: string; sms_billing: SmsBilling }>(
      "PATCH",
      `/api/brands/${slug}/sms-billing`,
      { cost_per_credit: costPerCredit, currency },
    ),

  listAudits: (slug: string) =>
    request<AuditSummary[]>("GET", `/api/brands/${slug}/audits`),
  getAudit: (slug: string, auditId: string) =>
    request<AuditDetail>("GET", `/api/brands/${slug}/audits/${auditId}`),
  startAudit: (slug: string, kind: AuditKind, connectedAccountId?: string) =>
    request<AuditSummary>("POST", `/api/brands/${slug}/audits`, {
      kind,
      connected_account_id: connectedAccountId ?? null,
    }),
  // Foundation audit runs the producer-DAG orchestrator (not a single skill).
  // force=true re-runs within the cooldown (the UI confirms first).
  startFoundationAudit: (slug: string, force = false) =>
    request<FoundationAuditResult>(
      "POST",
      `/api/brands/${slug}/audits/foundation`,
      { force },
    ),

  // Audit Report (Plan 2) — always-on, deterministic CH-only report.
  // Region-aware like getPerformance: multi-region brands have separate
  // Klaviyo accounts, so the query carries ?region= and the page keys its
  // useQuery on slug+region+days (cache invariant). Default window is 90d
  // (content-pillar analysis needs the longer window). The endpoint never
  // 500s — each section degrades with its own `_status`.
  getAuditReport: (
    slug: string,
    params: { days?: number; region?: string | null } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.days != null) qs.set("days", String(params.days));
    if (params.region) qs.set("region", params.region);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<AuditReport>(
      "GET",
      `/api/brands/${slug}/audit-report${suffix}`,
    );
  },

  // Audit Log (2026-06-19) — read-only action history + outcomes.
  auditLog: (slug: string, limit = 200) =>
    request<AuditLogResponse>(
      "GET",
      `/api/brands/${slug}/audit-log?limit=${limit}`,
    ),

  // Performance split by country (region + date-range aware, dashboard-exact
  // from eventconsolidated). `dateRange` is the preferred scope (honors custom
  // start/end); `days` kept for legacy callers. `unavailable` flags metrics with
  // no per-country value (email sends → no send-denominator rates by country).
  getCountryPerformance: (
    slug: string,
    params: {
      days?: number;
      dateRange?: DateRangeValue;
      region?: string | null;
      compareValue?: CompareValue;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.dateRange) {
      // dateRangeQs encodes date_range=custom&start_date=&end_date= (or preset);
      // merge its parts in so the backend _resolve_window honors the filter.
      for (const part of dateRangeQs(params.dateRange).split("&")) {
        const [k, v] = part.split("=");
        if (k) qs.set(k, decodeURIComponent(v ?? ""));
      }
    } else if (params.days != null) {
      qs.set("days", String(params.days));
    }
    if (params.compareValue) {
      // compareQs encodes compare_mode=X (+ compare_start_date/compare_end_date
      // for custom); "" for mode "off". Merge in so the backend emits per-row
      // *_delta_pct fields. Mirrors how dateRangeQs parts are merged above.
      const compareFrag = compareQs(
        params.compareValue.mode,
        params.compareValue.startDate,
        params.compareValue.endDate,
      );
      if (compareFrag) {
        for (const part of compareFrag.split("&")) {
          const [k, v] = part.split("=");
          if (k) qs.set(k, decodeURIComponent(v ?? ""));
        }
      }
    }
    if (params.region) qs.set("region", params.region);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<CountryPerformance>(
      "GET",
      `/api/brands/${slug}/performance/countries${suffix}`,
    );
  },

  // NOTE (parity P0 2026-07-19): the retention-donor Actions client
  // (list/approve/reject/revert/defer + detail/bulk) was removed — no
  // /api/brands/{slug}/actions router exists in this backend. Re-add only
  // when a real Action model + queue lands (parity roadmap P2).

  // Flows (Plan 5 — FE-A) -------------------------------------------------
  listFlows: (slug: string, region?: string | null) => {
    const qs = region ? `?region=${encodeURIComponent(region)}` : "";
    return request<FlowsListResponse>("GET", `/api/brands/${slug}/flows${qs}`);
  },
  getFlow: (slug: string, flowId: string, region?: string | null) => {
    const qs = region ? `?region=${encodeURIComponent(region)}` : "";
    return request<FlowDetailResponse>(
      "GET",
      `/api/brands/${slug}/flows/${flowId}${qs}`,
    );
  },
  deployFlow: (slug: string, flowDefinitionId: string) =>
    request<FlowDeployResponse>(
      "POST",
      `/api/brands/${slug}/flows/${flowDefinitionId}/deploy`,
    ),

  // Campaigns (Plan 5 FE-B) ------------------------------------------------
  listCampaigns: (
    slug: string,
    withPerf: boolean = false,
    region?: string | null,
  ) => {
    const qs = new URLSearchParams();
    if (withPerf) qs.set("with_perf", "1");
    if (region) qs.set("region", region);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<CampaignSummary[]>(
      "GET",
      `/api/brands/${slug}/campaigns${suffix}`,
    );
  },
  // Region-aware: multi-region brands have separate Klaviyo accounts, so the
  // detail lookup must carry ?region= (mirrors listCampaigns). Without it the
  // backend falls back to "US-first" and a non-US campaign 404s.
  getCampaign: (slug: string, campaignId: string, region?: string | null) => {
    const qs = region ? `?region=${encodeURIComponent(region)}` : "";
    return request<CampaignDetail>(
      "GET",
      `/api/brands/${slug}/campaigns/${encodeURIComponent(campaignId)}${qs}`,
    );
  },

  // Segments + Templates (FE-C, Plan 5) ----------------------------------
  // Region-aware: multi-region brands like Sand & Sky have separate
  // Klaviyo accounts per region. Without ?region=, the backend resolves
  // the alphabetically-first active region — which means switching the
  // sidebar RegionPicker did nothing on these pages until now.
  listSegments: (
    slug: string,
    params: { limit?: number; offset?: number; region?: string | null } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    if (params.region) qs.set("region", params.region);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<PaginatedSegments>("GET", `/api/brands/${slug}/segments${suffix}`);
  },
  listTemplates: (
    slug: string,
    params: { limit?: number; offset?: number; region?: string | null } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    if (params.region) qs.set("region", params.region);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<PaginatedTemplates>("GET", `/api/brands/${slug}/templates${suffix}`);
  },

  // Onboarding (rebuilt on the real SP-API seller-connect backend, #53) -----
  createBrand: (payload: CreateBrandPayload) =>
    request<CreateBrandResponse>("POST", "/api/brands", payload),

  listSellerAccounts: () =>
    request<SellerAccountsResponse>("GET", "/api/onboarding/seller-accounts"),
  createSellerAccount: (payload: CreateSellerAccountPayload) =>
    request<CreateSellerAccountResponse>(
      "POST",
      "/api/onboarding/seller-accounts",
      payload,
    ),
  listBindings: () =>
    request<BindingsResponse>("GET", "/api/onboarding/bindings"),
  createBinding: (payload: CreateBindingPayload) =>
    request<CreateBindingResponse>("POST", "/api/onboarding/bindings", payload),

  // Step-3 first-value: synchronous single-brand SP-API re-poll. Row counts are
  // the visible "landing" evidence; ok=false (no MCP creds) is surfaced softly.
  analyticsRefresh: (slug: string, marketplace = "US") =>
    request<AnalyticsRefreshResponse>(
      "POST",
      `/api/brands/${slug}/analytics/refresh?marketplace=${encodeURIComponent(marketplace)}`,
    ),

  // FE-D advisor surfaces (Plan 6) ---------------------------------------
  getSubjects: (slug: string, region?: string | null) => {
    const qs = region ? `?region=${encodeURIComponent(region)}` : "";
    return request<SubjectsResponse>("GET", `/api/brands/${slug}/subjects${qs}`);
  },
  getPromotions: (slug: string, region?: string | null) => {
    const qs = region ? `?region=${encodeURIComponent(region)}` : "";
    return request<PromotionsResponse>("GET", `/api/brands/${slug}/promotions${qs}`);
  },
  getWeeklyReview: (slug: string) =>
    request<WeeklyReviewResponse>("GET", `/api/brands/${slug}/weekly-review`),
  triggerWeeklyReview: (slug: string) =>
    request<WeeklyReviewTriggerResponse>(
      "POST",
      `/api/brands/${slug}/weekly-review/trigger`,
    ),

  // Plan 8 — onboarding synthesis review --------------------------------
  getSynthesis: (slug: string) =>
    request<SynthesisResponse>("GET", `/api/brands/${slug}/synthesis`),

  // Plan 8 Phase F — Campaign Composer ----------------------------------
  composerGenerate: (slug: string, payload: ComposerGeneratePayload) =>
    request<ComposerGenerateResponse>(
      "POST",
      `/api/brands/${slug}/composer/generate`,
      payload,
    ),
  composerApprove: (slug: string, payload: ComposerApprovePayload) =>
    request<ComposerApproveResponse>(
      "POST",
      `/api/brands/${slug}/composer/approve`,
      payload,
    ),
  // Template assembly — audiences (lists+segments) and active slot maps.
  // Region-aware like listSegments: multi-region brands have separate
  // Klaviyo accounts, so the audiences query must carry ?region=.
  listAudiences: (
    slug: string,
    params: { region?: string | null } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.region) qs.set("region", params.region);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<AudiencesResponse>(
      "GET",
      `/api/brands/${slug}/audiences${suffix}`,
    );
  },
  listTemplateSlotMaps: (
    slug: string,
    params: { status?: string } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set("status", params.status);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<TemplateSlotMapSummary[]>(
      "GET",
      `/api/brands/${slug}/template-slot-maps${suffix}`,
    );
  },

  // Goals (Plan 6 / FE-E) ------------------------------------------------
  listGoals: (slug: string, includeArchived = false) => {
    const qs = includeArchived ? "?include_archived=true" : "";
    return request<Goal[]>("GET", `/api/brands/${slug}/goals${qs}`);
  },
  createGoal: (slug: string, payload: GoalCreatePayload) =>
    request<Goal>("POST", `/api/brands/${slug}/goals`, payload),
  patchGoal: (slug: string, goalId: string, payload: GoalPatchPayload) =>
    request<Goal>("PATCH", `/api/brands/${slug}/goals/${goalId}`, payload),
  deleteGoal: (slug: string, goalId: string) =>
    request<Goal>("DELETE", `/api/brands/${slug}/goals/${goalId}`),

  // Performance (Plan 6 / FE-E) -----------------------------------------
  // `dateRange` is the preferred way to scope the window; it emits
  // `date_range=<preset>` (or `date_range=custom&start_date=...&end_date=...`)
  // to the backend. Legacy callers passing `days` still work — the
  // backend accepts both for backwards compatibility.
  getPerformance: (
    slug: string,
    params: {
      days?: number;
      dateRange?: DateRangeValue;
      entityType?: PerformanceEntityType;
      region?: string | null;
      compareMode?: string;
      compareStart?: string;
      compareEnd?: string;
      channel?: "email" | "sms" | "all";
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.dateRange) {
      // dateRangeQs already encodes the params; merge them in.
      for (const part of dateRangeQs(params.dateRange).split("&")) {
        const [k, v] = part.split("=");
        if (k) qs.set(k, decodeURIComponent(v ?? ""));
      }
    } else if (params.days != null) {
      qs.set("days", String(params.days));
    }
    if (params.entityType) qs.set("entity_type", params.entityType);
    if (params.region) qs.set("region", params.region);
    if (params.compareMode) qs.set("compare_mode", params.compareMode);
    if (params.compareMode === "custom" && params.compareStart && params.compareEnd) {
      qs.set("compare_start_date", params.compareStart);
      qs.set("compare_end_date", params.compareEnd);
    }
    // Only send channel when it's a real scope; "all" is the blended default
    // but the backend default is "email", so we send it explicitly when chosen.
    if (params.channel) qs.set("channel", params.channel);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<PerformanceResponse>(
      "GET",
      `/api/brands/${slug}/performance${suffix}`,
    );
  },

  // Email vs SMS channel split. SMS (Klaviyo or SMSBump) is excluded from the
  // headline /performance reads, so this captures it for the channel card.
  getChannelBreakdown: (
    slug: string,
    params: {
      days?: number;
      dateRange?: DateRangeValue;
      region?: string | null;
      compareMode?: string;
      compareStart?: string;
      compareEnd?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.dateRange) {
      for (const part of dateRangeQs(params.dateRange).split("&")) {
        const [k, v] = part.split("=");
        if (k) qs.set(k, decodeURIComponent(v ?? ""));
      }
    } else if (params.days != null) {
      qs.set("days", String(params.days));
    }
    if (params.region) qs.set("region", params.region);
    if (params.compareMode) qs.set("compare_mode", params.compareMode);
    // Custom comparison window → explicit compare bounds.
    if (params.compareMode === "custom" && params.compareStart && params.compareEnd) {
      qs.set("compare_start_date", params.compareStart);
      qs.set("compare_end_date", params.compareEnd);
    }
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<ChannelBreakdownResponse>(
      "GET",
      `/api/brands/${slug}/performance/channels${suffix}`,
    );
  },

  // SMS-native economics grouped by provider (Klaviyo vs SMSBump/Attentive):
  // RPS, opt-out/failure/delivery rate, net list growth.
  getSmsEconomics: (
    slug: string,
    params: { days?: number; dateRange?: DateRangeValue; region?: string | null } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.dateRange) {
      for (const part of dateRangeQs(params.dateRange).split("&")) {
        const [k, v] = part.split("=");
        if (k) qs.set(k, decodeURIComponent(v ?? ""));
      }
    } else if (params.days != null) {
      qs.set("days", String(params.days));
    }
    if (params.region) qs.set("region", params.region);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<SmsEconomicsResponse>(
      "GET",
      `/api/brands/${slug}/sms/economics${suffix}`,
    );
  },

  // Monthly Shopify revenue split New/Repeat (13mo, orders_table) + CRM
  // (email/SMS) vs organic where Klaviyo events exist (recent only).
  getRevenueSegments: (slug: string) =>
    request<RevenueSegmentsResponse>(
      "GET",
      `/api/brands/${slug}/analytics/revenue-segments`,
    ),

  // V2 Feature B — portfolio comparison (median across sibling brands in
  // the same org). Used by the Performance page chart overlay and the
  // Dashboard RetentionAnalyticsCard. `brand_count: 0` ⇒ FE hides the
  // toggle / comparison rows.
  getPortfolioPerformance: (
    slug: string,
    params: { days?: number; dateRange?: DateRangeValue } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.dateRange) {
      for (const part of dateRangeQs(params.dateRange).split("&")) {
        const [k, v] = part.split("=");
        if (k) qs.set(k, decodeURIComponent(v ?? ""));
      }
    } else if (params.days != null) {
      qs.set("days", String(params.days));
    }
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<PortfolioPerformanceResponse>(
      "GET",
      `/api/brands/${slug}/performance/portfolio${suffix}`,
    );
  },

  // Admin (Plan 7 / FE-F) -------------------------------------------------
  getMyOrg: () => request<OrgMeResponse>("GET", "/api/orgs/me"),
  inviteMember: (orgId: string, payload: InviteMemberPayload) =>
    request<InviteMemberResponse>(
      "POST",
      `/api/orgs/${orgId}/members/invite`,
      payload,
    ),
  updateMemberRole: (orgId: string, userId: string, role: OrgRole) =>
    request<UpdateMemberRoleResponse>(
      "PATCH",
      `/api/orgs/${orgId}/members/${userId}`,
      { role },
    ),

  // Pending invites (org-admin).
  listOrgInvites: (orgId: string) =>
    request<{ items: OrgInvite[] }>("GET", `/api/orgs/${orgId}/invites`),
  resendOrgInvite: (orgId: string, inviteId: string) =>
    request<ResendInviteResponse>("POST", `/api/orgs/${orgId}/invites/${inviteId}/resend`),
  revokeOrgInvite: (orgId: string, inviteId: string) =>
    request<{ ok: boolean }>("POST", `/api/orgs/${orgId}/invites/${inviteId}/revoke`),

  // Member grants (brand access) + removal (org-admin).
  getMemberGrants: (orgId: string, userId: string) =>
    request<{ items: OrgMemberGrant[] }>(
      "GET",
      `/api/orgs/${orgId}/members/${userId}/grants`,
    ),
  replaceMemberGrants: (orgId: string, userId: string, brandIds: string[]) =>
    request<{ ok: boolean }>("PUT", `/api/orgs/${orgId}/members/${userId}/grants`, {
      // retention grants are channel-less — channel must be null.
      grants: brandIds.map((brand_id) => ({ brand_id, channel: null })),
    }),
  removeMember: (orgId: string, userId: string) =>
    request<{ ok: boolean }>("DELETE", `/api/orgs/${orgId}/members/${userId}`),

  // Org audit log (org-admin).
  getOrgAuditLog: (orgId: string, limit = 200) =>
    request<{ items: OrgAuditItem[] }>(
      "GET",
      `/api/orgs/${orgId}/audit-log?limit=${limit}`,
    ),

  billingStatus: () => request<BillingStatusResponse>("GET", "/api/billing/status"),

  // Customer analytics (Bloomreach-parity, 2026-05-05) -------------------
  getCohortRetention: (
    slug: string,
    params: { lookbackMonths?: number; maxMonthsSince?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.lookbackMonths != null) qs.set("lookback_months", String(params.lookbackMonths));
    if (params.maxMonthsSince != null) qs.set("max_months_since", String(params.maxMonthsSince));
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<CohortRetentionResponse>(
      "GET",
      `/api/brands/${slug}/analytics/cohort-retention${suffix}`,
    );
  },

  getSegmentDistribution: (slug: string) =>
    request<SegmentDistributionResponse>(
      "GET",
      `/api/brands/${slug}/analytics/segments`,
    ),

  // RFM tier distribution (P1.1, 2026-05-11) — on-demand CH compute.
  // 404 with detail="no_ch_coverage" when the brand has no CH database
  // mapped; caller should swallow + render an empty state.
  getRfmTierDistribution: (slug: string) =>
    request<RfmTierResponse>(
      "GET",
      `/api/brands/${slug}/rfm/tier-distribution`,
    ),

  getCustomerCohortKpis: (slug: string, lookbackDays = 90) =>
    request<CustomerCohortKpis>(
      "GET",
      `/api/brands/${slug}/analytics/customer-kpis?lookback_days=${lookbackDays}`,
    ),

  getRevenueTrend: (slug: string) =>
    request<RevenueTrendResponse>(
      "GET",
      `/api/brands/${slug}/analytics/revenue-trend`,
    ),

  // Historical CLV (P1.3 v1) -------------------------------------------
  getBrandClv: (slug: string) =>
    request<BrandClvResponse>("GET", `/api/brands/${slug}/clv`),

  getListHealth: (slug: string, days = 30) =>
    request<ListHealthResponse>(
      "GET",
      `/api/brands/${slug}/analytics/list-health?days=${days}`,
    ),

  getDeliverabilityAlerts: (slug: string) =>
    request<DeliverabilityAlertsResponse>(
      "GET",
      `/api/brands/${slug}/analytics/deliverability-alerts`,
    ),


  getPortfolioDeliverabilityAlerts: () =>
    request<PortfolioDeliverabilityResponse>(
      "GET",
      `/api/portfolio/deliverability-alerts`,
    ),

  // Retention-coverage gaps (2026-06-16) — brand-wide, all-region.
  // All 404 with no_ch_coverage on CH-less brands → cards hide (retry:false).
  getReachability: (slug: string) =>
    request<ReachabilityResponse>(
      "GET",
      `/api/brands/${slug}/reachability`,
    ),

  getEarlyRepeat: (slug: string) =>
    request<EarlyRepeatResponse>(
      "GET",
      `/api/brands/${slug}/early-repeat`,
    ),

  getSubscriberFunnel: (slug: string) =>
    request<SubscriberFunnelResponse>(
      "GET",
      `/api/brands/${slug}/subscriber-funnel`,
    ),

  getDiscountLtv: (slug: string) =>
    request<DiscountLtvResponse>(
      "GET",
      `/api/brands/${slug}/promotions/discount-ltv`,
    ),

  getBrandLearnings: (slug: string, limit = 10) =>
    request<BrandLearningsResponse>(
      "GET",
      `/api/brands/${slug}/analytics/learnings?limit=${limit}`,
    ),

  getRecentExecuted: (slug: string, limit = 5) =>
    request<RecentExecutedResponse>(
      "GET",
      `/api/brands/${slug}/analytics/recent-executed?limit=${limit}`,
    ),

  // Cross-brand portfolio triage (P2.1) ---------------------------------
  getPortfolioTopActions: (limit = 10) =>
    request<PortfolioTopActionsResponse>(
      "GET",
      `/api/portfolio/top-actions?limit=${limit}`,
    ),

  adminListOrgs: () => request<AdminOrgRow[]>("GET", "/api/admin/orgs"),
  adminListUsers: () => request<AdminUserRow[]>("GET", "/api/admin/users"),
  adminListGrants: () =>
    request<AdminGrantRow[]>("GET", "/api/admin/permission-grants"),
  adminRevokeGrant: (grantId: string) =>
    request<{ id: string; revoked_at: string }>(
      "DELETE",
      `/api/admin/permission-grants/${grantId}`,
    ),
  adminListBrandMappings: () =>
    request<AdminBrandMappingList>("GET", "/api/admin/brands/warehouse-mapping"),
  adminSetBrandMapping: (
    brandId: string,
    body: { warehouse_brand: string | null; catalogue_brands: string[] },
  ) =>
    request<SetWarehouseMappingResponse>(
      "PUT",
      `/api/admin/brands/${brandId}/warehouse-mapping`,
      body,
    ),

  // P2.1 — Product Insights (top products by revenue/units/buyers/attach/repeat)
  getProductInsights: (
    slug: string,
    params: ProductInsightsParams = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.sort) qs.set("sort", params.sort);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<ProductInsightsResponse>(
      "GET",
      `/api/brands/${slug}/catalog/product-analysis${suffix}`,
    );
  },

  // 2026-05-14 — LTV by product (V1 acquisition + V2 any-purchase + V3 attributed)
  getProductLtv: (
    slug: string,
    // `from`/`to` match the sibling product-analysis convention
    // (getProductInsights) and the backend's `from`/`to` aliased query params
    // (which map to fetch_product_ltv's date_from/date_to).
    params: { limit?: number; sort?: ProductLtvSort; from?: string; to?: string } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.sort) qs.set("sort", params.sort);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<ProductLtvResponse>(
      "GET",
      `/api/brands/${slug}/catalog/product-ltv${suffix}`,
    );
  },

  // V4 — Product-cohort retention matrix for ONE product.
  getProductCohortRetention: (
    slug: string,
    productKey: string,
    params: { lookback_months?: number; max_months_since?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    qs.set("product_key", productKey);
    if (params.lookback_months !== undefined)
      qs.set("lookback_months", String(params.lookback_months));
    if (params.max_months_since !== undefined)
      qs.set("max_months_since", String(params.max_months_since));
    return request<ProductCohortRetentionResponse>(
      "GET",
      `/api/brands/${slug}/catalog/product-ltv/cohort-retention?${qs.toString()}`,
    );
  },

  // Impact dashboard (Phase 0, 2026-05-11) -----------------------------
  getBrandImpact: (slug: string, params: BrandImpactParams = {}) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.compare_from) qs.set("compare_from", params.compare_from);
    if (params.compare_to) qs.set("compare_to", params.compare_to);
    if (params.conversion_metric)
      qs.set("conversion_metric", params.conversion_metric);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<BrandImpactResponse>(
      "GET",
      `/api/brands/${slug}/impact${suffix}`,
    );
  },

  // Category insights (P2.2, 2026-05-11) -------------------------------
  getCategoryInsights: (slug: string, params: CategoryInsightsParams = {}) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.groupBy) qs.set("group_by", params.groupBy);
    if (params.limit != null) qs.set("limit", String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<CategoryInsightsResponse>(
      "GET",
      `/api/brands/${slug}/catalog/category-analysis${suffix}`,
    );
  },

  // Monitors (P3) ------------------------------------------------------
  listMonitors: (slug: string) =>
    request<Monitor[]>("GET", `/api/brands/${slug}/monitors`),
  createMonitor: (slug: string, payload: MonitorCreatePayload) =>
    request<Monitor>("POST", `/api/brands/${slug}/monitors`, payload),
  getMonitor: (slug: string, monitorId: string) =>
    request<Monitor>("GET", `/api/brands/${slug}/monitors/${monitorId}`),
  updateMonitor: (slug: string, monitorId: string, payload: MonitorPatchPayload) =>
    request<Monitor>(
      "PATCH",
      `/api/brands/${slug}/monitors/${monitorId}`,
      payload,
    ),
  deleteMonitor: (slug: string, monitorId: string) =>
    request<void>("DELETE", `/api/brands/${slug}/monitors/${monitorId}`),
  listBreaches: (slug: string, monitorId: string, limit = 30) =>
    request<MonitorBreach[]>(
      "GET",
      `/api/brands/${slug}/monitors/${monitorId}/breaches?limit=${limit}`,
    ),
  runMonitor: (slug: string, monitorId: string) =>
    request<MonitorRunResponse>(
      "POST",
      `/api/brands/${slug}/monitors/${monitorId}/run`,
    ),

  // P4.3 Best Cross-Sell Date ---------------------------------------------
  // CH-compute-only next-order prediction from per-brand orders_table.
  // 404 detail="no_ch_coverage" when brand has no CH map; caller should
  // swallow + render an empty state.
  getDueCustomers: (
    slug: string,
    opts?: { limit?: number; min_orders?: number },
  ) => {
    const qs = new URLSearchParams();
    if (opts?.limit !== undefined) qs.set("limit", String(opts.limit));
    if (opts?.min_orders !== undefined)
      qs.set("min_orders", String(opts.min_orders));
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<DueCustomersResponse>(
      "GET",
      `/api/brands/${slug}/recommendations/cross-sell-date/due-customers${suffix}`,
    );
  },

  // Replenishment (2026-05-31) — persisted forecast cohorts. Region-aware,
  // mirrors listCampaigns(?region=). One endpoint serves both the Overview
  // radar card (summary) and the Replenishment page (cohorts).
  //
  // `dateRange` (2026-06-25, OPTIONAL) makes the headline `total_due` count
  // range-aware (7D/30D/3M). Omitted → backend defaults to 30d, byte-for-byte
  // identical to the pre-range behaviour — so the standalone Replenishment
  // page (which passes no dateRange) is unchanged. The radar card on the
  // Overview dashboard passes the dashboard's active dateRange.
  getReplenishment: (
    slug: string,
    region?: string | null,
    dateRange?: DateRangeValue,
  ) => {
    const params = new URLSearchParams();
    if (region) params.set("region", region);
    if (dateRange) {
      // Reuse dateRangeQs so preset/custom encoding matches every other card.
      for (const part of dateRangeQs(dateRange).split("&")) {
        const [k, v] = part.split("=");
        if (k && v !== undefined) params.set(k, decodeURIComponent(v));
      }
    }
    const qs = params.toString() ? `?${params.toString()}` : "";
    return request<ReplenishmentResponse>(
      "GET",
      `/api/brands/${slug}/replenishment${qs}`,
    );
  },

  getCrossSellDateSummary: (
    slug: string,
    opts?: { min_orders?: number },
  ) => {
    const qs = new URLSearchParams();
    if (opts?.min_orders !== undefined)
      qs.set("min_orders", String(opts.min_orders));
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<CrossSellDateSummaryResponse>(
      "GET",
      `/api/brands/${slug}/recommendations/cross-sell-date/summary${suffix}`,
    );
  },

  // P4.1 — Next-Best-Product (recommendations) -------------------------
  getNextBestProductForCustomer: (
    slug: string,
    customerEmail: string,
    params: Omit<NextBestProductParams, "customer_email"> = {},
  ) => {
    const qs = new URLSearchParams();
    qs.set("customer_email", customerEmail);
    if (params.top_n !== undefined) qs.set("top_n", String(params.top_n));
    if (params.lookback_days !== undefined)
      qs.set("lookback_days", String(params.lookback_days));
    return request<NextBestProductResponse>(
      "GET",
      `/api/brands/${slug}/recommendations/next-best-product?${qs.toString()}`,
    );
  },

  getTopCopurchasePairs: (
    slug: string,
    params: TopCopurchasePairsParams = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.lookback_days !== undefined)
      qs.set("lookback_days", String(params.lookback_days));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<TopCopurchasePairsResponse>(
      "GET",
      `/api/brands/${slug}/recommendations/next-best-product/top-pairs${suffix}`,
    );
  },

  // P4.2 Personalized Send-Time (2026-05-11) ----------------------------
  // Brand-level + per-profile peak engagement hour (UTC).
  // Sibling-coordinated route: lead merges into a unified
  // /api/brands/{slug}/recommendations/* router post-merge.

  getBrandSendTime: (slug: string, params: SendTimeParams = {}) => {
    const qs = new URLSearchParams();
    if (params.lookback_days != null) qs.set("lookback_days", String(params.lookback_days));
    if (params.min_opens_threshold != null)
      qs.set("min_opens_threshold", String(params.min_opens_threshold));
    if (params.region) qs.set("region", params.region);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<SendTimeFullResponse>(
      "GET",
      `/api/brands/${slug}/recommendations/send-time${suffix}`,
    );
  },

  getBrandSendTimeSummary: (slug: string, params: SendTimeParams = {}) => {
    const qs = new URLSearchParams();
    if (params.lookback_days != null) qs.set("lookback_days", String(params.lookback_days));
    if (params.min_opens_threshold != null)
      qs.set("min_opens_threshold", String(params.min_opens_threshold));
    if (params.region) qs.set("region", params.region);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<SendTimeBrandSummaryResponse>(
      "GET",
      `/api/brands/${slug}/recommendations/send-time/brand-summary${suffix}`,
    );
  },

  getProfileSendTime: (
    slug: string,
    customerEmail: string,
    params: SendTimeParams = {},
  ) => {
    const qs = new URLSearchParams();
    qs.set("customer_email", customerEmail);
    if (params.lookback_days != null) qs.set("lookback_days", String(params.lookback_days));
    if (params.min_opens_threshold != null)
      qs.set("min_opens_threshold", String(params.min_opens_threshold));
    if (params.region) qs.set("region", params.region);
    return request<SendTimeProfileResponse>(
      "GET",
      `/api/brands/${slug}/recommendations/send-time?${qs.toString()}`,
    );
  },

  // P5 Playbooks -------------------------------------------------------
  listPlaybooks: (slug: string, category?: PlaybookCategory) => {
    const qs = category ? `?category=${encodeURIComponent(category)}` : "";
    return request<PlaybooksListResponse>(
      "GET",
      `/api/brands/${slug}/playbooks${qs}`,
    );
  },
  getPlaybook: (slug: string, playbookId: string) =>
    request<PlaybookSummary>("GET", `/api/brands/${slug}/playbooks/${playbookId}`),
  instantiatePlaybook: (
    slug: string,
    playbookId: string,
    inputs: Record<string, unknown>,
  ) =>
    request<PlaybookInstantiateResponse>(
      "POST",
      `/api/brands/${slug}/playbooks/${playbookId}/instantiate`,
      { inputs },
    ),

  // Wave 7 Setup Checklist ---------------------------------------------
  getSetupChecklist: (slug: string) =>
    request<SetupChecklistResponse>(
      "GET",
      `/api/brands/${slug}/setup-checklist`,
    ),

  // Wave 8 — Predicted Traits (churn + forward CLV) --------------------
  // Heuristic v1. All endpoints 404 with detail="no_ch_coverage" when
  // brand has no CH map or insufficient orders for prediction; caller
  // should swallow + render empty/hidden state with `retry: false`.
  getForwardClv: (slug: string, customerEmail: string) => {
    const qs = new URLSearchParams({ customer_email: customerEmail });
    return request<ForwardClvPrediction>(
      "GET",
      `/api/brands/${slug}/predictions/clv?${qs.toString()}`,
    );
  },
  getForwardClvSummary: (slug: string) =>
    request<ForwardClvSummary>(
      "GET",
      `/api/brands/${slug}/predictions/clv/summary`,
    ),

  // Wave 8.1 — Klaviyo native Predictive Analytics ---------------------
  // CH-first / SDK-fallback. 404 detail="no_klaviyo_connection" when
  // brand isn't connected; "no_predictive_analytics" when Klaviyo
  // doesn't have predictions for this customer.
  getKlaviyoPa: (slug: string, customerEmail: string, region?: string) => {
    const qs = new URLSearchParams({ customer_email: customerEmail });
    if (region) qs.set("region", region);
    return request<KlaviyoPaPrediction>(
      "GET",
      `/api/brands/${slug}/predictions/klaviyo-pa?${qs.toString()}`,
    );
  },
  getKlaviyoPaSummary: (slug: string, region?: string) => {
    const qs = new URLSearchParams();
    if (region) qs.set("region", region);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<KlaviyoPaSummary>(
      "GET",
      `/api/brands/${slug}/predictions/klaviyo-pa/summary${suffix}`,
    );
  },

  // Wave 9 — Attribution Reports (last-touch v1) ----------------------
  // 404 detail="no_ch_coverage" when the brand has no CH database mapped
  // OR the warehouse has no Klaviyo event data; caller should swallow +
  // render an empty state with `retry: false`.
  getAttributionByChannel: (
    slug: string,
    params: { from: string; to: string },
  ) => {
    const qs = new URLSearchParams(params);
    return request<AttributionByChannelResponse>(
      "GET",
      `/api/brands/${slug}/attribution/by-channel?${qs.toString()}`,
    );
  },

  getAttributionByCampaign: (
    slug: string,
    params: { from: string; to: string; limit?: number },
  ) => {
    const qs = new URLSearchParams({
      from: params.from,
      to: params.to,
      ...(params.limit ? { limit: String(params.limit) } : {}),
    });
    return request<AttributionByCampaignResponse>(
      "GET",
      `/api/brands/${slug}/attribution/by-campaign?${qs.toString()}`,
    );
  },

  // Wave 8.2.4 — Segment membership preview ----------------------------------
  // Fetches a sample of up to ``limit`` members from the named Klaviyo
  // segment with their native predictive_analytics (predicted CLV +
  // churn probability). Backend reads metadata from CH (name, member_count)
  // and member profiles from the live Klaviyo SDK.
  //
  // 404 ``no_klaviyo_connection`` — brand not connected to Klaviyo.
  // 404 ``segment_not_found``    — segment deleted/archived in Klaviyo.
  // 502 ``klaviyo_unavailable``  — Klaviyo API error (timeout, 5xx).
  getSegmentPreview: (
    slug: string,
    segmentId: string,
    limit?: number,
    region?: string,
  ) => {
    const qs = new URLSearchParams();
    if (limit != null) qs.set("limit", String(limit));
    if (region) qs.set("region", region);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<SegmentPreview>(
      "GET",
      `/api/brands/${slug}/segments/${encodeURIComponent(segmentId)}/preview${suffix}`,
    );
  },

  // 2026-05-13 — segment_profiles_raw-backed insights
  getSegmentInsights: (slug: string, segmentId: string, region?: string) => {
    const qs = new URLSearchParams();
    if (region) qs.set("region", region);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<SegmentInsightsResponse>(
      "GET",
      `/api/brands/${slug}/segments/${encodeURIComponent(segmentId)}/insights${suffix}`,
    );
  },
  getSegmentSuppressionAudit: (slug: string, segmentId: string, region?: string) => {
    const qs = new URLSearchParams();
    if (region) qs.set("region", region);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<SegmentSuppressionAuditResponse>(
      "GET",
      `/api/brands/${slug}/segments/${encodeURIComponent(segmentId)}/suppression-audit${suffix}`,
    );
  },
  getSegmentsOverlap: (slug: string, segmentIds: string[], region?: string) => {
    const qs = new URLSearchParams({ segments: segmentIds.join(",") });
    if (region) qs.set("region", region);
    return request<SegmentsOverlapResponse>(
      "GET",
      `/api/brands/${slug}/segments/overlap?${qs}`,
    );
  },
  searchCustomer: (slug: string, email: string, region?: string) => {
    const qs = new URLSearchParams({ email });
    if (region) qs.set("region", region);
    return request<CustomerSearchResponse>(
      "GET",
      `/api/brands/${slug}/customers/search?${qs}`,
    );
  },

  // Wave 8.2.2 — CH freshness SLO for the brand's PA table
  getKlaviyoFreshness: (slug: string) =>
    request<KlaviyoFreshnessResponse>(
      "GET",
      `/api/brands/${slug}/predictions/klaviyo-pa/freshness`,
    ),

  // Wave 8.2.3 — Dormancy monitor: high-CLV + updated-stale customers
  getKlaviyoDormancy: (
    slug: string,
    opts: { clv_percentile?: number; stale_days?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (opts.clv_percentile !== undefined)
      qs.set("clv_percentile", String(opts.clv_percentile));
    if (opts.stale_days !== undefined)
      qs.set("stale_days", String(opts.stale_days));
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<DormantHighClvResponse>(
      "GET",
      `/api/brands/${slug}/predictions/klaviyo-pa/dormancy${suffix}`,
    );
  },

  // Executive Overview — cross-market roll-up for multi-region brands.
  // Read-only aggregation composed server-side from per-region service calls;
  // the frontend issues ONE call (not N) and renders the cached payload.
  getOverview: (slug: string) =>
    request<OverviewPayload>("GET", `/api/brands/${slug}/overview`),

  // Chat (Plan C — SDK migration) -----------------------------------------
  // The streaming turn itself goes through `streamChat` in lib/sse.ts
  // (@microsoft/fetch-event-source — POST + SSE). These three are the
  // non-streaming companions that the ChatPanel uses for continuity:
  //   - listChatSessions: enumerate a brand's conversations (history reload).
  //   - getChatMessages:  hydrate one conversation's persisted turns.
  //   - resolveChatPermission: answer a permission_request SSE frame.
  listChatSessions: (brandSlug: string) =>
    request<ChatSessionsListResponse>(
      "GET",
      `/api/chat/sessions?brand_slug=${encodeURIComponent(brandSlug)}`,
    ),
  getChatMessages: (conversationId: string) =>
    request<ChatMessagesResponse>(
      "GET",
      `/api/chat/sessions/${encodeURIComponent(conversationId)}/messages`,
    ),
  resolveChatPermission: (
    conversationId: string,
    toolUseId: string,
    decision: "allow" | "deny",
    message?: string,
  ) =>
    request<ChatPermissionResolveResponse>(
      "POST",
      `/api/chat/sessions/${encodeURIComponent(conversationId)}/permission`,
      { tool_use_id: toolUseId, decision, ...(message ? { message } : {}) },
    ),

  // Translate (2026-06-18 — clone-and-translate) ---------------------------
  // Region-aware like listCampaigns/listSegments: multi-region brands have
  // separate Klaviyo accounts, so target suggestions + the draft carry ?region=.
  getTranslateTargets: (slug: string, region?: string | null) => {
    const qs = region ? `?region=${encodeURIComponent(region)}` : "";
    return request<TranslateTargetsResponse>(
      "GET",
      `/api/brands/${slug}/translate/targets${qs}`,
    );
  },
  createTranslationDraft: (
    slug: string,
    entityType: TranslateEntityType,
    entityId: string,
    body: { target_locales: string[]; region?: string | null; engine?: TranslateEngine },
  ) =>
    request<TranslationDraft>(
      "POST",
      `/api/brands/${slug}/translate/${entityType}/${encodeURIComponent(entityId)}/draft`,
      body,
    ),
  listTranslationDrafts: (slug: string, status?: TranslationDraftStatus | string) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return request<TranslationDraftSummary[]>(
      "GET",
      `/api/brands/${slug}/translate/drafts${qs}`,
    );
  },
  getTranslationDraft: (slug: string, id: string) =>
    request<TranslationDraft>(
      "GET",
      `/api/brands/${slug}/translate/drafts/${encodeURIComponent(id)}`,
    ),
  patchTranslationDraft: (slug: string, id: string, edits: TranslationEdit[]) =>
    request<TranslationDraft>(
      "PATCH",
      `/api/brands/${slug}/translate/drafts/${encodeURIComponent(id)}`,
      { edits },
    ),
  approveTranslationDraft: (
    slug: string,
    id: string,
    audienceByLocale: Record<string, string[]>,
  ) =>
    request<TranslateApproveResponse>(
      "POST",
      `/api/brands/${slug}/translate/drafts/${encodeURIComponent(id)}/approve`,
      { audience_ids_by_locale: audienceByLocale },
    ),
  getTranslateSettings: (slug: string) =>
    request<TranslationSettings>(
      "GET",
      `/api/brands/${slug}/translate/settings`,
    ),
  putTranslateSettings: (slug: string, body: TranslationSettings) =>
    request<TranslationSettings>(
      "PUT",
      `/api/brands/${slug}/translate/settings`,
      body,
    ),
};

// ---------------------------------------------------------------------------
// Wave 4.1 — Monthly-grain monitor. Separately exported (NOT merged into
// `api`) to keep the parent `api` object literal untouched while two
// sibling worktrees (tier-2-funnel, tier-2-mta) also append in parallel.
// Consumers can call `monthlyMonitorApi.get(slug, ...)` directly.
// ---------------------------------------------------------------------------

import type {
  MonthlyMonitorResponse,
  MonthlySurface,
} from "./types";

export const monthlyMonitorApi = {
  get: (
    slug: string,
    opts: { surface?: MonthlySurface; region?: string } = {},
  ) => {
    const qs = new URLSearchParams();
    if (opts.surface) qs.set("surface", opts.surface);
    if (opts.region) qs.set("region", opts.region);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<MonthlyMonitorResponse>(
      "GET",
      `/api/brands/${slug}/monitors/monthly${suffix}`,
    );
  },
};

// ---------------------------------------------------------------------------
// Wave 9.1 — Multi-touch attribution. Separately exported (same pattern as
// monthlyMonitorApi) to keep the parent `api` object untouched. Falls back
// to v1 `getAttributionByChannel` on 404 at the call site.
// ---------------------------------------------------------------------------

import type {
  AttributionModel,
  AttributionMultitouchResponse,
} from "./types";

export const mtaApi = {
  getMultitouch: (
    slug: string,
    params: {
      from: string;
      to: string;
      model: AttributionModel;
      region?: string;
    },
  ) => {
    const qs = new URLSearchParams({
      from: params.from,
      to: params.to,
      model: params.model,
    });
    if (params.region) qs.set("region", params.region);
    return request<AttributionMultitouchResponse>(
      "GET",
      `/api/brands/${slug}/attribution/multitouch?${qs.toString()}`,
    );
  },
};
